import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ZorroTableComponent } from '../../../../components/common/custom-table/zorro-table.component';
import { NoDataComponent } from '../../../../components/common/no-data/no-data.component';

type ImpactLevel = 'h' | 'm' | 'l';

export interface RiskItem {
  module: string;
  test_case: string;
  suggested_action: string;
}

export interface RiskGroup {
  impact: string;
  high_likelyhood: RiskItem[];
  medium_likelyhood: RiskItem[];
  low_likelyhood: RiskItem[];
}

@Component({
  selector: 'app-risk-indication',
  standalone: true,
  imports: [CommonModule, NoDataComponent, ZorroTableComponent],
  templateUrl: './risk-indication.component.html',
  styleUrl: './risk-indication.component.scss',
})
export class RiskIndicationComponent implements OnInit, OnChanges {
  @Input() metricDisplayName: string = 'Impact Based Test Suggestion';

  @Input() data: RiskGroup[] = [
    {
      impact: 'High Impact',
      high_likelyhood: [],
      low_likelyhood: [
        {
          module: 'Fund Transfer Module',
          test_case: 'Verify successful fund transfer to a valid external account with sufficient funds.',
          suggested_action: 'Monitor',
        },
      ],
      medium_likelyhood: [],
    },
    {
      impact: 'Medium Impact',
      high_likelyhood: [],
      low_likelyhood: [
        {
          module: 'Transfer Module',
          test_case: 'Verify transfer of the minimum allowed amount.',
          suggested_action: 'Monitor',
        },
      ],
      medium_likelyhood: [],
    },
    { impact: 'Low Impact', high_likelyhood: [], medium_likelyhood: [], low_likelyhood: [] },
  ];

  impact: ImpactLevel = 'h';
  likelihood: ImpactLevel = 'h';
  tableRows: (RiskItem & { id: number })[] = [];
  sectionTitle: string = '';

  readonly tableColumns = [
    {
      display_name: 'Module',
      key: 'module',
      headerStyles: {
        width: '22%',
      }
    },
    {
      display_name: 'Test Case',
      key: 'test_case',
      headerStyles: {
        width: '38%',
      }
    },
    {
      display_name: 'Suggested Action',
      key: 'suggested_action',
    },
  ];

  readonly tableOptions = { pageSize: 10, showPagination: true };

  get recordCount(): number {
    return this.tableRows.length;
  }

  ngOnInit(): void {
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.refresh();
    }
  }

  setImpact(level: ImpactLevel): void {
    this.impact = level;
    this.refresh();
  }

  setLikelihood(level: ImpactLevel): void {
    this.likelihood = level;
    this.refresh();
  }

  private labelOf(level: ImpactLevel): string {
    const labels: Record<ImpactLevel, string> = { h: 'High', m: 'Medium', l: 'Low' };
    return labels[level];
  }

  private refresh(): void {
    const impactLabel = this.labelOf(this.impact);
    const likelihoodLabel = this.labelOf(this.likelihood);
    const likelihoodKey = `${likelihoodLabel.toLowerCase()}_likelyhood` as keyof RiskGroup;
    const group = this.data?.find(d => d.impact === `${impactLabel} Impact`);
    const rawRows: RiskItem[] = group ? ((group[likelihoodKey] as RiskItem[]) ?? []) : [];
    this.tableRows = rawRows.map((row, index) => ({ ...row, id: index + 1 }));
    this.sectionTitle = `${impactLabel} Impact & ${likelihoodLabel} Likelihood`;
  }
}
